import React from 'react';
import {
  withStyles,
  Theme,
  WithStyles,
  createStyles,
  Chip,
} from '@material-ui/core';

import { TOPIC_CLUSTER_LABEL } from 'ha/constants/CommunityCategories';
import { useIntl } from 'ha/i18n';
import { Markdown } from 'ha/components/Markdown/Markdown';
import { getReadingTime } from 'ha/modules/Contentful/utils';
import { Grid, Typography, units } from '@ha/dsl';

import { ShortCityData, ShortCountryData } from 'ha/modules/Hermes/types';
import { BlogAuthorCard } from './BlogAuthorCard';
import { Author } from '../types';

const styles = (theme: Theme) =>
  createStyles({
    header: {
      [`@supports (
          (background-clip: text) or (-webkit-background-clip: text)
        ) and (
          (text-fill-color: transparent) or (-webkit-text-fill-color: transparent)
        )`]: {
        background: 'linear-gradient(105deg, #442f80, #ef96a1);',
        backgroundClip: 'text',
        textFillColor: 'transparent',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fallbacks: {
          background: theme.palette.primary.main,
        },
      },
      marginBottom: units.rem(1.5),
      [theme.breakpoints.up('lg')]: {
        marginBottom: units.rem(2),
      },
    },
    wrapper: {
      marginBottom: units.rem(2.5),
    },
    h2: { marginBottom: units.rem(1) },
  });

interface Props {
  content: string;
  author: Author;
  isUpdated?: boolean;
  hideReadingTime: boolean;
  'data-test-locator'?: string;
  topicCluster?: string;
  country?: ShortCountryData | undefined;
  city?: ShortCityData | undefined;
}

const ContentInternal: React.FC<Props & WithStyles<typeof styles>> = ({
  content,
  author,
  isUpdated,
  classes,
  hideReadingTime,
  topicCluster,
  country,
  city,
  ...props
}) => {
  const { urlResolver, T } = useIntl();
  const readingTime = getReadingTime(hideReadingTime, [content]);

  const url = React.useMemo(() => {
    let returnURL;
    if (city && country) {
      returnURL = urlResolver.formatHrefURL(
        urlResolver.getCityUrl(country.name, city.name!),
      );
    } else if (country) {
      returnURL = urlResolver.formatHrefURL(
        urlResolver.getCountryUrl(country.name),
      );
    }

    if (topicCluster) {
      returnURL = `${returnURL}#${topicCluster}`;
    }

    return returnURL;
  }, [city, country, topicCluster, urlResolver]);

  return (
    <Markdown
      content={content}
      components={{
        h2: ({ children }: { children: React.ReactNode }) => (
          <a id={children?.toString()} href={`#${children?.toString()}`}>
            <Typography.H2 component="h2" className={classes.h2}>
              {children}
            </Typography.H2>
          </a>
        ),
        h1: ({ children }: { children: React.ReactNode }) => (
          <Grid
            container
            direction="column"
            spacing={2}
            className={classes.wrapper}
          >
            <Grid item>
              <Typography.H1
                component="h1"
                className={classes.header}
                data-test-locator="Blog Content Header"
              >
                {children}
              </Typography.H1>
            </Grid>
            <Grid item>
              <BlogAuthorCard
                author={author}
                readingTime={readingTime}
                isUpdated={isUpdated}
              />
            </Grid>
            {topicCluster && (city || country) && (
              <Grid item>
                <Chip
                  label={T(TOPIC_CLUSTER_LABEL[topicCluster])}
                  component="a"
                  href={url}
                  clickable
                />
              </Grid>
            )}
          </Grid>
        ),
      }}
      {...props}
    />
  );
};

const Content = withStyles(styles)(ContentInternal);
export { Content };
